// Sugalarm — kiosk in-app no-data alarm banner (cycle 360.5).
//
// The always-foregrounded Android kiosk tablet sounds the distinct `noData`
// tone in the WebView when the CGM feed goes dark (see the cycle-360.5 driver
// in `useAlarmSounds`). **Cycle 386: that tone plays ONCE, not on a loop** —
// which makes THIS BANNER the thing that persists for the outage, not the
// sound. It is mounted FRAME-LEVEL (like the cycle-97 BroadcastBanner) so it
// is visible on EVERY screen: now that the audio is a single cue, a kiosk on
// some other screen would otherwise have nothing left to show a dark feed.
//
// Kiosk-only by construction: `noDataAlarmActive` is only ever true when the
// hook's `noDataLoopEnabled` (= `variant === "kiosk"`) is set, so the parent
// only mounts this on the kiosk frame.
//
// Silence is OUTAGE-KEYED in the hook: tapping it clears the CURRENT outage
// only — a later outage always re-alarms, and data returning auto-clears it,
// so this can never permanently mute the no-data alert. A live glucose alarm
// always wins (the hook's gate stands the cue down while `episode.isLive`), so
// this banner and a real low/high banner are never both up.
//
// Visual: the urgent alarm-banner ground, pulsing dot, message, Silence.
//
// CYCLE 453.1 — SILENCE IS AN ALARM BUTTON LIKE EVERY OTHER (452.2's rules).
// It was 36 px of white on `--zone-urgent`: 2.78:1 in every kiosk theme. It
// now takes the shared look in `alarmBannerStyle.ts` — the urgent ground and
// ink, the white primary pill (44 px, dark-red ink) — and the strip is the
// 52 px banner row, the height of the Low / High / Urgent banners.
//
// CYCLE 401 — IT SITS AT THE BOTTOM, WHERE EVERY OTHER ALARM BANNER SITS.
// It used to render directly under the kiosk header while cycle 394 had
// already moved the Low/High/Urgent strip to the bottom edge, so the one
// banner that persists for a whole outage was also the one in a different
// place. It is still mounted FRAME-LEVEL (a dark feed must be visible off the
// Now screen); only its position moved, via `.kioskBottomStrip` — see the
// rationale in `tokens.css`, including why it is a child of `.kioskContent`
// rather than of the `.kiosk` column.
//
// ⚠ THE HEIGHT IS `ALARM_BANNER_ROW_PX` (52), the row every alarm banner
// uses. KioskFrame lifts the SpeakingBanner by `--kiosk-alarm-banner-height`
// while this banner is mounted, so a voice caption can never cover Silence —
// the cycle-394 collision. Pinned by `alarmStripLift.test.ts`.

import { createPortal } from "react-dom";

import {
  ALARM_BANNER_ROW_PX,
  alarmBannerDot,
  alarmBannerGround,
  alarmPrimaryButton,
} from "./alarmBannerStyle";

interface Props {
  onSilence: () => void;
  /** Cycle 402: the bedside clock FACE is currently showing.
   *
   *  ⚠ THIS IS NOT "clock mode is on" AND THE DIFFERENCE IS LOAD-BEARING.
   *  The face only renders on `/now`; with clock mode enabled but the user
   *  on Settings, the normal chrome is up and this banner must stay the
   *  in-flow bottom strip. `LowAlarmBanner` can read `useClockMode()`
   *  directly because NowScreen only ever renders it on `/now` — this
   *  banner is FRAME-level and shows on every screen, so it must be told.
   *  App.tsx owns the one expression (`clockMode.active && pathname ===
   *  "/now"`) that both this and `showChrome` read. */
  clockFaceActive?: boolean;
  /** Cycle 453.1: the frame draws no chrome here (signed-in `/login`, the
   *  Android pairing takeover). `.kioskContent` still reserves the bottom-nav
   *  safe-area though no nav is drawn, so the banner sits flush on the
   *  stage's bottom edge instead — the off-Now banner's
   *  `.kioskAlarmBannerFlush` (452.2), the same rule. */
  flush?: boolean;
  /** Cycle 453.2 (operator decision 3, option a): a takeover that paints
   *  OUTSIDE the kiosk stage (the reminder, z 9000) is up. The stage is its
   *  own stacking context, so no z-index inside it can put this banner over
   *  that takeover — the banner leaves the stage instead and draws in a
   *  body-level layer sized and scaled EXACTLY like the stage (same box,
   *  same `transform: scale()`, same centring), flush on its bottom edge.
   *  One instance throughout: the frame's mount sites are unchanged. */
  topLayer?: { scale: number; width: number; height: number } | null;
}

export function NoDataAlarmBanner({
  onSilence,
  clockFaceActive = false,
  flush = false,
  topLayer = null,
}: Props) {
  const lifted = topLayer != null && typeof document !== "undefined";
  const strip = (
    <div
      role="alert"
      aria-live="assertive"
      // `.alarmBannerStrip` keeps the strip above any kiosk chrome (cycle
      // 85/103 defensive floor; no-op off `.kiosk`).
      // Cycle 402: the in-flow bottom-strip class is for the NORMAL frame.
      // On the clock face the strip is fixed to the face's bottom edge
      // instead, and `order` / `margin-top` are meaningless for an
      // out-of-flow element.
      data-alarm-control="nodata-banner"
      data-alarm-bottom-band=""
      className={
        lifted
          ? "alarmBannerStrip kioskAlarmTopLayerStrip"
          : clockFaceActive
          ? "alarmBannerStrip"
          : `alarmBannerStrip kioskBottomStrip${flush ? " kioskAlarmBannerFlush" : ""}`
      }
      style={{
        flexShrink: 0,
        boxSizing: "border-box",
        height: ALARM_BANNER_ROW_PX,
        padding: "0 16px",
        ...alarmBannerGround("urgent"),
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "inherit",
        fontWeight: 700,
        fontSize: "var(--type-floor)",
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        boxShadow: "0 1px 0 rgba(0, 0, 0, 0.18)",
        // Cycle 402: on the bedside face, pin to its bottom edge. COPIED
        // PROPERTY-FOR-PROPERTY from `LowAlarmBanner`'s cycle-167 clock
        // override (and `HighAlarmBanner` / `UrgentBanner`'s, which cycle
        // 394 aligned to it) rather than re-derived, so the four strips
        // cannot drift apart. Shadow flips upward off the bottom edge.
        // Stays in the z-50 band: below AlarmSilenceGuard (60) and
        // UrgentOverlay (100).
        //
        // ⚠ WHAT `fixed` RESOLVES AGAINST DEPENDS ON THE SCALE, and both
        // cases land in the same place on real hardware. KioskFrame sets
        // `transform: scale()` on `.kiosk` ONLY when the scale is not 1
        // (cycle 53/199 keeps the Pi byte-identical), and a transform makes
        // an element the containing block for `fixed` descendants. So on a
        // scaled panel this pins to the STAGE's bottom edge; on the
        // unscaled 1024x600 Pi there is no transform and it pins to the
        // VIEWPORT, which the stage fills in kiosk fullscreen. This is not
        // new or special to this banner — the cycle-167 Low/High clock
        // strips have shipped on exactly this behaviour.
        //
        // ⚠ THE FACE MUST RESERVE THIS BAND OR THE STRIP LANDS ON THE CLOCK
        // DIGITS — cycle 394's rule. NowScreen feeds `reserveBottomStrip`
        // off the same `noDataAlarmActive` this banner mounts on.
        ...(clockFaceActive && !lifted
          ? {
              position: "fixed" as const,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 50,
              boxShadow: "0 -1px 0 rgba(0, 0, 0, 0.18)",
            }
          : null),
      }}
    >
      <span aria-hidden style={alarmBannerDot(true)} />
      <span
        style={{
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          flex: "1 1 auto",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        No CGM data — check the sensor
      </span>
      <button
        type="button"
        onClick={onSilence}
        aria-label="Silence the no-data alarm for this outage"
        style={{ ...alarmPrimaryButton("urgent"), flexShrink: 0 }}
      >
        Silence
      </button>
    </div>
  );
  if (!lifted || topLayer == null) return strip;
  return createPortal(
    <div className="kioskAlarmTopLayer" data-alarm-top-layer="">
      <div
        className="kioskAlarmTopLayerBox"
        style={{
          width: topLayer.width,
          height: topLayer.height,
          transform: topLayer.scale !== 1 ? `scale(${topLayer.scale})` : undefined,
        }}
      >
        {strip}
      </div>
    </div>,
    document.body,
  );
}
